import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spin } from 'antd';
import { SearchOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import axios from 'axios';
import config from '../../config';
import { getUserToken } from '../../utils/helpers';
import { ThemeModal } from '../Styles/Theme';
import {
  ATModalContent,
  QuestionSearchWrapper,
  QuestionDropdown,
} from './CreateCampaignStyle';

// Helper to clean question text (remove %%NUMBER%% placeholders)
const cleanQuestionText = (text) => {
  if (!text) return '';
  return text.replace(/%+\d+%+/g, '').trim();
};

const AdvancedTargetingModal = ({
  visible,
  onClose,
  onSave,
  editingItem,
  existingQuestionCodes = [], // Already added question codes to filter out
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [options, setOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  // Get the current editing question code (to allow it in the list when editing)
  const editingQuestionCode = editingItem?.qualification_code;

  // Fetch questions on search
  const fetchQuestions = useCallback(async (search = '') => {
    setQuestionsLoading(true);
    try {
      const response = await axios.get(
        `${config.api.base_url}/purespectrumSync/advanced-targeting/questions`,
        {
          params: { search },
          headers: { Authorization: 'Bearer ' + getUserToken() },
        }
      );
      if (response.data?.status === 1) {
        setQuestions(response.data.data?.questions || []);
      }
    } catch (error) {
      console.error('Error fetching AT questions:', error);
      setQuestions([]);
    } finally {
      setQuestionsLoading(false);
    }
  }, []);

  // Fetch options for selected question
  const fetchOptions = useCallback(async (qualificationCode) => {
    setOptionsLoading(true);
    try {
      const response = await axios.get(
        `${config.api.base_url}/purespectrumSync/advanced-targeting/questions/${qualificationCode}`,
        {
          headers: { Authorization: 'Bearer ' + getUserToken() },
        }
      );
      if (response.data?.status === 1) {
        const data = response.data.data;
        setOptions(data?.options || []);
        setSelectedQuestion({
          qualification_code: data.qualification_code,
          name: data.name,
          question_text: data.question_text,
          buyer_text: data.buyer_text,
        });
      }
    } catch (error) {
      console.error('Error fetching AT options:', error);
      setOptions([]);
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  // Load questions when modal opens (not editing)
  useEffect(() => {
    if (visible && !editingItem) {
      fetchQuestions('');
    }
  }, [visible, editingItem, fetchQuestions]);

  // Initialize when editing
  useEffect(() => {
    if (visible && editingItem) {
      setSelectedQuestion({
        qualification_code: editingItem.qualification_code,
        name: editingItem.qualification_name,
        question_text: editingItem.question_text,
      });
      setSelectedOptions(
        editingItem.condition_codes.map((code, idx) => ({
          condition_code: code,
          label: editingItem.selected_labels?.[idx] || code,
          value: String(code),
        }))
      );
      fetchOptions(editingItem.qualification_code);
    }
  }, [visible, editingItem, fetchOptions]);

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      setSearchValue('');
      setQuestions([]);
      setShowDropdown(false);
      setSelectedQuestion(null);
      setOptions([]);
      setSelectedOptions([]);
    }
  }, [visible]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        searchRef.current &&
        !searchRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (showDropdown) {
        fetchQuestions(searchValue);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue, showDropdown, fetchQuestions]);

  const handleSearchFocus = () => {
    setShowDropdown(true);
  };

  const handleQuestionSelect = (question) => {
    setSelectedQuestion(question);
    setShowDropdown(false);
    setSearchValue('');
    setSelectedOptions([]);
    fetchOptions(question.qualification_code);
  };

  const handleOptionToggle = (option) => {
    setSelectedOptions((prev) => {
      // Compare as strings to handle type mismatch
      const exists = prev.find((o) => String(o.condition_code) === String(option.condition_code));
      if (exists) {
        return prev.filter((o) => String(o.condition_code) !== String(option.condition_code));
      }
      return [...prev, option];
    });
  };

  const handleSelectAll = () => {
    setSelectedOptions([...options]);
  };

  const handleClearAll = () => {
    setSelectedOptions([]);
  };

  const handleRemoveTag = (conditionCode) => {
    setSelectedOptions((prev) =>
      prev.filter((o) => String(o.condition_code) !== String(conditionCode))
    );
  };

  const handleSave = () => {
    if (!selectedQuestion || selectedOptions.length === 0) return;

    onSave({
      qualification_code: selectedQuestion.qualification_code,
      qualification_name: selectedQuestion.name,
      question_text: selectedQuestion.question_text,
      condition_codes: selectedOptions.map((o) => String(o.condition_code)),
      selected_labels: selectedOptions.map((o) => o.label),
    });
  };

  const isOptionSelected = (option) => {
    // Compare as strings to handle type mismatch (editing has strings, API returns numbers)
    return selectedOptions.some((o) => String(o.condition_code) === String(option.condition_code));
  };

  const canSave = selectedQuestion && selectedOptions.length > 0;

  return (
    <ThemeModal
      title={editingItem ? 'Edit Definition' : 'Add Definition'}
      open={visible}
      onCancel={onClose}
      onOk={handleSave}
      width={640}
      destroyOnClose
      centered
      okText="Save"
      cancelText="Cancel"
      okButtonProps={{ disabled: !canSave }}
    >
      <ATModalContent>
        {/* Search / Question Selection */}
        {!selectedQuestion && (
          <div className="modal-search" style={{ position: 'relative' }}>
            <QuestionSearchWrapper ref={searchRef}>
              <SearchOutlined className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search questions..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onFocus={handleSearchFocus}
                autoFocus
              />
            </QuestionSearchWrapper>

            {showDropdown && (
              <QuestionDropdown ref={dropdownRef}>
                {questionsLoading ? (
                  <div className="loading">
                    <Spin size="small" /> Loading questions...
                  </div>
                ) : (() => {
                  // Filter out already added questions (except the one being edited)
                  const availableQuestions = questions.filter((q) => {
                    if (editingQuestionCode && q.qualification_code === editingQuestionCode) {
                      return true;
                    }
                    return !existingQuestionCodes.includes(q.qualification_code);
                  });

                  if (availableQuestions.length === 0) {
                    return (
                      <div className="no-results">
                        {searchValue ? 'No questions found' : 'No questions available'}
                      </div>
                    );
                  }

                  return availableQuestions.map((q) => (
                    <div
                      key={q.qualification_code}
                      className="dropdown-item"
                      onClick={() => handleQuestionSelect(q)}
                    >
                      <div className="item-name">{q.name}</div>
                    </div>
                  ));
                })()}
              </QuestionDropdown>
            )}
          </div>
        )}

        {/* Selected Question & Options */}
        {selectedQuestion && (
          <>
            <div className="question-text">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <strong>{selectedQuestion.name}</strong>
                  {(selectedQuestion.buyer_text || selectedQuestion.question_text) && cleanQuestionText(selectedQuestion.buyer_text || selectedQuestion.question_text) && (
                    <div style={{ marginTop: 4, fontSize: 13, color: '#485066' }}>
                      {cleanQuestionText(selectedQuestion.buyer_text || selectedQuestion.question_text)}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSelectedQuestion(null);
                    setOptions([]);
                    setSelectedOptions([]);
                    setShowDropdown(true);
                    fetchQuestions('');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#3d2969',
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  Change
                </button>
              </div>
            </div>

            {optionsLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin /> Loading options...
              </div>
            ) : (
              <>
                <div className="options-header">
                  <span className="options-count">
                    {selectedOptions.length} of {options.length} selected
                  </span>
                  <div className="options-actions">
                    <button className="action-btn" onClick={handleSelectAll}>
                      Select All
                    </button>
                    <button className="action-btn clear-btn" onClick={handleClearAll}>
                      Clear All
                    </button>
                  </div>
                </div>

                <div className="options-list">
                  {options.map((option) => {
                    const selected = isOptionSelected(option);
                    return (
                      <div
                        key={option.condition_code}
                        className={`option-item ${selected ? 'selected' : ''}`}
                        onClick={() => handleOptionToggle(option)}
                      >
                        <div className={`option-checkbox ${selected ? 'checked' : ''}`}>
                          {selected && <CheckOutlined />}
                        </div>
                        <span className="option-label">{option.label}</span>
                      </div>
                    );
                  })}
                </div>

                {selectedOptions.length > 0 && (
                  <div className="selected-tags">
                    <div className="selected-tags-title">
                      Selected ({selectedOptions.length}):
                    </div>
                    {selectedOptions.map((opt) => (
                      <span key={opt.condition_code} className="selected-tag">
                        {opt.label}
                        <CloseOutlined
                          className="remove-tag"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveTag(opt.condition_code);
                          }}
                        />
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </ATModalContent>
    </ThemeModal>
  );
};

export default AdvancedTargetingModal;
